import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import { cond, debounce } from 'lodash';
import PropTypes from 'prop-types';
import Preloader from '@omilia/preloader';

import { fileType } from 'types';

import { getHasFlowEnabled } from 'store/services/project/selectors';
import * as selectors from 'store/services/flow/selectors';
import * as actions from 'store/services/flow/actions';

import Splitter from 'components/Splitter';
import StaticTree from 'components/Tree';
import UnitsSearchResult from 'components/Units/UnitsSearchResult';

import useBoolean from 'utils/hooks/useToggle';
import useDebounce from 'utils/hooks/useDebounce';
import FilesAndFoldersHeader from './FilesAndFoldersHeader';
import UploadFilesModal from './components/UploadFilesModal';
import EmptyTreeView from './EmptyTreeView';
import DeleteModal from './DeleteModal';

const Editor = React.lazy(() =>
  import(
    /* webpackChunkName: "Editor" */ 'components/FileSystem/components/Editor'
  ),
);

const otherwise = () => true;

const StateFullTree = cond([
  [({ isLoading }) => isLoading, () => <Preloader />],
  [
    ({ listItems }) => listItems.length === 0,
    ({ createNewTempFile, createNewTempFolder }) => (
      <EmptyTreeView
        onCreateFileClick={createNewTempFile}
        onCreateFolderClick={createNewTempFolder}
      />
    ),
  ],
  [
    otherwise,
    ({
      listItems,
      onDoubleClickItem,
      query,
      renderNoSearchResults,
      width,
      onUpload,
    }) => (
      <StaticTree
        data={listItems}
        query={query}
        renderNoSearchResults={renderNoSearchResults}
        width={width}
        onDoubleClickItem={onDoubleClickItem}
        onUpload={onUpload}
      />
    ),
  ],
]);

export const Flow = ({
  hasFlowEnabled,
  closeEditorFile,
  isLoadingEditor,
  isLoadingList,
  isSaving,
  openFile,
  exportAllFilesAndFolders,
  editorSearchTerm,
  deleteFiles,
  selectedFile,
  selectedFiles,
  setTheme,
  theme,
  updateBlob,
  updateEditorSearchTerm,
  updateFiles,
  listItems,
  getFilesAndFolders,
  createNewTempFile,
  createNewTempFolder,
}) => {
  const [uploadPath, setUploadPath] = useState('');
  const [paneSize, setPaneSize] = useState();
  const [query, setQuery] = useState('');
  const [deleteVisibility, setDeleteVisibility] = useState(false);
  const debouncedQuery = useDebounce(query, 500);
  const debouncedSetPaneSize = debounce(setPaneSize, 50);

  useEffect(() => {
    getFilesAndFolders();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const [
    uploadFileModalIsOpen,
    { reset: closeUploadFileModal, toggle: toggleUploadFileModal },
  ] = useBoolean(false);

  if (hasFlowEnabled === false) {
    return <Redirect to="/" />;
  }

  const onDoubleClick = item => {
    if (!item.isFolder) {
      const normalizedItem = {
        name: item.data.title,
        id: item.id,
        path: item.id,
      };
      openFile(normalizedItem);
    }
  };

  const handleDeleteFiles = async () => {
    await deleteFiles(listItems);
    setDeleteVisibility(false);
  };

  const renderNoSearchResults = searchTerm => (
    <UnitsSearchResult
      items={[]}
      searchTerm={searchTerm}
      showAll={() => setQuery('')}
      unitName="term"
    />
  );

  const onUploadHandler = path => {
    setUploadPath(path);
    toggleUploadFileModal();
  };

  return (
    <Splitter split="vertical" onChange={debouncedSetPaneSize}>
      <div css="height:100%">
        <FilesAndFoldersHeader
          createNewTempFile={createNewTempFile}
          createNewTempFolder={createNewTempFolder}
          deleteAll={() => setDeleteVisibility(true)}
          exportAll={exportAllFilesAndFolders}
          getItems={setQuery}
          items={listItems}
          modelName="flow"
          searchTerm={query}
          onOpenModal={() => onUploadHandler('')}
        />

        {deleteVisibility && (
          <DeleteModal
            onCloseRequest={() => setDeleteVisibility(false)}
            onDelete={handleDeleteFiles}
          />
        )}
        {uploadFileModalIsOpen && (
          <UploadFilesModal
            closeModal={closeUploadFileModal}
            path={uploadPath}
          />
        )}
        <StateFullTree
          createNewTempFile={createNewTempFile}
          createNewTempFolder={createNewTempFolder}
          isLoading={isLoadingList}
          listItems={listItems}
          query={debouncedQuery}
          renderNoSearchResults={renderNoSearchResults}
          width={paneSize}
          onDoubleClickItem={onDoubleClick}
          onOpenModal={toggleUploadFileModal}
          onUpload={path => onUploadHandler(path)}
        />
      </div>

      <React.Suspense fallback={<Preloader />}>
        <Editor
          shouldLoadEditor
          closeEditorFile={closeEditorFile}
          isLoading={isLoadingEditor}
          isSaving={isSaving}
          openFile={openFile}
          searchTerm={editorSearchTerm}
          selectedFile={selectedFile}
          selectedFiles={selectedFiles}
          setTheme={setTheme}
          theme={theme}
          updateBlob={updateBlob}
          updateEditorSearchTerm={updateEditorSearchTerm}
          updateFiles={updateFiles}
        />
      </React.Suspense>
    </Splitter>
  );
};

Flow.propTypes = {
  closeEditorFile: PropTypes.func.isRequired,
  deleteFiles: PropTypes.func.isRequired,
  editorSearchTerm: PropTypes.string.isRequired,
  exportAllFilesAndFolders: PropTypes.func.isRequired,
  getFilesAndFolders: PropTypes.func.isRequired,
  hasFlowEnabled: PropTypes.bool.isRequired,
  isLoadingEditor: PropTypes.bool.isRequired,
  isLoadingList: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  openFile: PropTypes.func.isRequired,
  selectedFile: fileType.isRequired,
  selectedFiles: PropTypes.arrayOf(fileType).isRequired,
  updateBlob: PropTypes.func.isRequired,
  updateEditorSearchTerm: PropTypes.func.isRequired,
  updateFiles: PropTypes.func.isRequired,
  createNewTempFile: PropTypes.func,
  createNewTempFolder: PropTypes.func,
  listItems: PropTypes.arrayOf(PropTypes.object),
  setTheme: PropTypes.func,
  theme: PropTypes.string,
};

const mapStateToProps = state => ({
  listItems: selectors.getAllFiles(state),
  listSearchTerm: selectors.getSearchTerm(state),
  editorSearchTerm: selectors.getEditorSearchTerm(state),
  theme: selectors.getTheme(state),
  selectedFile: selectors.getEditorCurrentFile(state),
  selectedFiles: selectors.getEditorFiles(state),
  isSaving: selectors.getIsSaving(state),
  isLoadingEditor: selectors.getIsEditorLoading(state),
  isLoadingList: selectors.getIsLoading(state),
  hasFlowEnabled: getHasFlowEnabled(state),
});

const mapDispatchToProps = {
  openFile: actions.openFile,
  closeEditorFile: actions.closeEditorFile,
  setTheme: actions.setTheme,
  updateBlob: actions.updateBlob,
  updateEditorSearchTerm: actions.updateEditorSearchTerm,
  getFilesAndFolders: actions.getFilesAndFolders,
  updateFiles: actions.updateFiles,
  exportAllFilesAndFolders: actions.exportAllFilesAndFolders,
  addFile: actions.addFile,
  updateFile: actions.updateFile,
  openFiles: actions.openFiles,
  deleteFiles: actions.deleteFiles,
  createNewTempFile: actions.createNewTempFile,
  createNewTempFolder: actions.createNewTempFolder,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Flow);
